<template>
  <AppForm
    ref="form"
    :rules="rules"
    :values="owner"
    v-slot:default="{ formErrors }"
  >
    <div class="p-fluid p-formgrid p-grid">
      <div class="p-field p-col-12">
        <AppFormItem :error="formErrors.name" label="Nome">
          <InputText v-model="owner.name" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-6">
        <AppFormItem label="Documento" :error="formErrors.document">
          <InputText
            v-model="owner.document"
            v-mask="['###.###.###-##', '##.###.###/####-##']"
          />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-6">
        <AppFormItem label="Data de nascimento">
          <Calendar
            v-model="owner.birthDate"
            dateFormat="dd/mm/yy"
            showIcon
            :monthNavigator="true"
            :yearNavigator="true"
            :yearRange="birthDateRange"
            :locale="$locales.calendar.ptBR"
            :showOnFocus="false"
            v-mask="'##/##/####'"
          />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-6">
        <AppFormItem label="Telefone">
          <InputText v-model="owner.phone" v-mask="'(##) ####-####'" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-6">
        <AppFormItem label="Celular">
          <InputText v-model="owner.cellPhone" v-mask="'(##) #####-####'" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-12">
        <AppFormItem label="Email" :error="formErrors.email">
          <InputText v-model="owner.email" />
        </AppFormItem>
      </div>

      <div class="p-field p-col-12 p-md-12" style="margin-bottom: 0">
        <div class="p-card-title">Endereço</div>
      </div>
      <div class="p-field p-col-12 p-md-6">
        <AppFormItem label="CEP">
          <InputText
            v-model="owner.address.zipCode"
            v-mask="'#####-###'"
            @blur="getAddress()"
          />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-6">
        <AppFormItem label="Estado">
          <AppAutoComplete
            v-model="owner.address.state"
            :options="states"
            valueField="code"
          ></AppAutoComplete>
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-6">
        <AppFormItem label="Cidade">
          <InputText v-model="owner.address.city" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-6">
        <AppFormItem label="Bairro">
          <InputText v-model="owner.address.district" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-12">
        <AppFormItem label="Endereço">
          <InputText v-model="owner.address.address" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-6">
        <AppFormItem label="Numero">
          <InputText v-model="owner.address.number" />
        </AppFormItem>
      </div>
      <div class="p-field p-col-12 p-md-6">
        <AppFormItem label="Complemento">
          <InputText v-model="owner.address.complement" />
        </AppFormItem>
      </div>
    </div>
  </AppForm>
</template>

<script>
import { debounce } from 'debounce';
import { STATES, getAddress } from '../../../lib/address';
import { validateCPF, validateCNPJ, validateEmail } from '../../../lib/helpers';
import { OwnerApi } from '../../../services/api';

export default {
  name: 'OwnerForm',
  props: {
    id: Number,
    loading: Boolean,
    loadingSave: Boolean,
  },
  data() {
    return {
      states: STATES,
      rules: {
        name: [{ required: true, message: 'Informe o nome.' }],
        document: {
          validator(rule, value, callback) {
            value = value.replace(/\D/g, '');
            if (!value) {
              return callback();
            }
            if (value.length === 11 && validateCPF(value)) {
              return callback();
            }
            if (value.length === 14 && validateCNPJ(value)) {
              return callback();
            }
            callback(new Error('Informe um documento válido.'));
          },
        },
        email: {
          validator(rule, value, callback) {
            if (!value || validateEmail(value)) {
              return callback();
            }
            callback(new Error('Informe um email válido.'));
          },
        },
      },
      owner: {
        name: '',
        document: '',
        birthDate: null,
        phone: '',
        cellPhone: '',
        email: '',
        address: {
          zipCode: '',
          state: '',
          city: '',
          district: '',
          address: '',
          number: '',
          complement: '',
        },
      },
      birthDateRange: `${this.$dayjs()
        .subtract(100, 'y')
        .format('YYYY')}:${this.$dayjs().format('YYYY')}`,
    };
  },
  async created() {
    if (!this.id) {
      return;
    }
    await this.fetchOwner();
  },
  methods: {
    getAddress: debounce(async function fn() {
      if (this.owner?.address?.zipCode?.length < 8) {
        return;
      }
      const address = await getAddress(this.owner?.address?.zipCode);
      if (address) {
        this.owner.address = { ...this.owner.address, ...address };
      }
    }, 500),
    async fetchOwner() {
      try {
        this.$emit('update:loading', true);
        const owner = await OwnerApi.fetchById(this.id);
        let { document } = owner;

        if (document && document.length === 14) {
          document = this.$formatCNPJ(document);
        } else if (document) {
          document = this.$formatCPF(document);
        }

        if (owner?.address?.zipCode) {
          owner.address.zipCode = this.$formatZipCode(owner.address.zipCode);
        }

        this.owner = {
          ...owner,
          birthDate: owner.birthDate ? new Date(owner.birthDate) : null,
          phone: owner.phone ? this.$formatPhone(owner.phone) : null,
          cellPhone: owner.cellPhone
            ? this.$formatPhone(owner.cellPhone)
            : null,
          document,
        };
      } finally {
        this.$emit('update:loading', false);
      }
    },
    async save() {
      try {
        if (!(await this.$refs.form.validate())) {
          return;
        }

        this.$emit('update:loadingSave', true);
        const birthDate = this.owner.birthDate
          ? this.owner.birthDate.toISOString().split('T')[0]
          : null;

        const body = {
          ...this.owner,
          birthDate,
        };

        console.log('body', JSON.stringify(body, null, 2));

        if (!this.id) {
          await OwnerApi.create(body);
        } else {
          await OwnerApi.update(this.id, body);
        }

        this.$emit('saved');
      } catch (error) {
        this.handleError(error);
      } finally {
        this.$emit('update:loadingSave', false);
      }
    },
    handleError(error) {
      console.log(error);
      let detail = 'Não foi possível salvar o proprietário.';
      this.$toast.add({
        severity: 'error',
        summary: 'Desculpe!',
        detail,
        life: 3000,
      });
    },
  },
};
</script>

<style>
</style>